import { WELLNESS_DOMAINS_DISPLAY_LABELS, WELLNESS_SUB_DOMAINS_DISPLAY_LABELS, WELLNESS_SUBDOMAINS_PER_DOMAIN } from '@/constants';
import { THealthDomain, THealthSubDomain } from '@/types/Health.types';

// Content carries multiple health domains + subdomains. The taxonomy is the same
// one the wellness screener uses (sourced from the backend assessment subdomain
// enums), so we reuse the existing wellness constants rather than duplicate them.
// The backend validates published content's subdomains against this exact union.

export const CONTENT_DOMAINS: readonly THealthDomain[] = ['mental', 'physical', 'social', 'spiritual', 'vocational', 'purpose', 'fun'];

// 'physical' -> 'Physical'
export const formatDomainLabel = (domain: THealthDomain): string => WELLNESS_DOMAINS_DISPLAY_LABELS[domain] ?? domain;

// 'PHYSICAL_ACTIVITY' -> 'Physical activity' (falls back to a humanized form).
export const formatSubdomainLabel = (subdomain: string): string =>
  WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[subdomain as THealthSubDomain] ??
  subdomain
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const getSubdomainsForDomain = (domain?: THealthDomain | null): readonly string[] =>
  domain ? (WELLNESS_SUBDOMAINS_PER_DOMAIN[domain] ?? []) : [];

// Parent domain a subdomain belongs to (e.g. 'HEALTH_CHECKUPS' -> 'physical').
export const getDomainForSubdomain = (subdomain: string): THealthDomain | undefined =>
  CONTENT_DOMAINS.find((domain) => getSubdomainsForDomain(domain).includes(subdomain));

// Subdomains grouped by their parent domain, for only the selected domains —
// feeds the sectioned subdomain dropdown.
export const getSubdomainSectionsForDomains = (domains: THealthDomain[]): Record<string, readonly string[]> =>
  Object.fromEntries(domains.map((domain) => [domain, WELLNESS_SUBDOMAINS_PER_DOMAIN[domain] ?? []]));

// Union of valid subdomains across all selected domains (deduped, order preserved).
export const getSubdomainsForDomains = (domains: THealthDomain[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const domain of domains) {
    for (const subdomain of getSubdomainsForDomain(domain)) {
      if (!seen.has(subdomain)) {
        seen.add(subdomain);
        result.push(subdomain);
      }
    }
  }
  return result;
};
